import { useTranslation } from 'react-i18next';

/**
 * Validates a string to ensure it does not contain leading or trailing spaces
 * and only includes JSON stringify-able characters.
 *
 * @param value - The input value to validate.
 * @returns A string containing the error message if validation fails, or null if validation passes.
 */
export function validateString(value: string, checkSpace: boolean): string | null {
  const trimmedValue = value.trim();
  if (trimmedValue !== value) {
    return 'validation.leadingTrailingSpaces';
  }
  if (checkSpace && /\s/.test(trimmedValue)) {
    return 'validation.noSpacesAllowed';
  }

  try {
    JSON.stringify({ value: trimmedValue });
  } catch (e) {
    return 'validation.invalidCharacters';
  }
  return null;
}

/**
 * Validates and corrects metadata keys by removing leading/trailing spaces
 * and ensuring they are valid for use as object keys.
 *
 * @param key - The metadata key to validate and correct
 * @returns An object with the corrected key and any validation errors
 */
export function validateAndCorrectMetadataKey(key: string): {
  correctedKey: string;
  error: string | null;
  hasChanges: boolean;
} {
  const trimmedKey = key.trim();
  const hasChanges = trimmedKey !== key;
  
  // Check if key is empty after trimming
  if (!trimmedKey) {
    return {
      correctedKey: '',
      error: 'validation.metadataKeyEmpty',
      hasChanges
    };
  }

  // Check if key contains invalid characters for object keys
  if (!/^[a-zA-Z0-9_\u00E4\u00F6\u00FC\u00DF\u00C4\u00D6\u00DC\s-]+$/.test(trimmedKey)) {
    return {
      correctedKey: trimmedKey,
      error: 'validation.metadataKeyInvalidCharacters',
      hasChanges
    };
  }

  // Check if key is too long (max 100 characters)
  if (trimmedKey.length > 100) {
    return {
      correctedKey: trimmedKey,
      error: 'validation.metadataKeyTooLong',
      hasChanges
    };
  }

  return {
    correctedKey: trimmedKey,
    error: null,
    hasChanges
  };
}

/**
 * Validates a string to ensure it is a valid hex color code.
 *
 * @param value - The input value to validate.
 * @returns A string containing the error message if validation fails, or null if validation passes.
 */
export function validateHexColor(value: string): string | null {
  if (!/^#([0-9A-Fa-f]{3}){1,2}$/.test(value)) {
    return `Der Wert "${value}" ist kein gültiger Hexwert`;
  }
  return null;
}
